import { collaborationBundleConfig } from './collaboration';
import { devopsBundleConfig } from './devops';
import { devopsEssentialsBundleConfig } from './devopsEssentials';
import { serviceManagementBundleConfig } from './serviceManagement';
import { testManagementBundleConfig } from './testManagement';

import type { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';
import type React from 'react';

export interface HeroConfig {
  breadcrumbTitle: string;
  title: string;
  description: string;
  requestButton?: string;
  headerImage: string;
  headerImageAlt: string;
  heroImage: string;
  heroImageAlt: string;
}

export interface AppConfig {
  title: string;
  description: string;
  icon: string;
  iconAlt: string;
  url: string;
}

export type AppsConfig = AppConfig[];

export interface BenefitItem {
  title: string;
  description: string;
  iconAlt: string;
  icon?: string;
}

export interface BenefitsConfig {
  title: string;
  description?: string;
  items: BenefitItem[];
}

export interface AppDetails {
  title: string;
  vendor: string;
  partnerTier?: MarketplacePartnerTierType;
  rating: string;
  reviewCount: string;
  installOrDownloadsText: string;
  badge?: 'cloud-fortified' | 'cloud-security';
  icon?: React.ReactNode;
  url?: string;
}

export interface AppSection {
  title: string;
  description: string;
  appDetails: AppDetails;
  videoId: string;
}

export interface AppsSectionConfig {
  title: string;
  description?: string;
  sections: AppSection[];
}

export interface FooterConfig {
  title: string;
  description: string;
  requestButton?: string;
  leftImageAlt: string;
  rightImageAlt: string;
  leftImage: string;
  rightImage: string;
  footerCTA?: {
    text: string;
    url: string;
    type: 'internal' | 'external';
  };
}

export interface BundleConfig {
  hero: HeroConfig;
  apps: AppsConfig;
  benefits: BenefitsConfig;
  appsSection: AppsSectionConfig;
  footer: FooterConfig;
}

export interface BundlesConfig {
  [bundleId: string]: BundleConfig;
}

export function bundlesConfig(enableIssueTerminology: boolean): BundlesConfig {
  return {
    devops: devopsBundleConfig(enableIssueTerminology),
    collaboration: collaborationBundleConfig(enableIssueTerminology),
    serviceManagement: serviceManagementBundleConfig(enableIssueTerminology),
    testManagement: testManagementBundleConfig(enableIssueTerminology),
    devopsEssentials: devopsEssentialsBundleConfig(enableIssueTerminology),
    // Add more bundles here as needed
  };
}

export const getBundleConfig = (
  bundleId: string,
  enableIssueTerminology: boolean
): BundleConfig => {
  const config = bundlesConfig(enableIssueTerminology)[bundleId];
  if (!config) {
    throw new Error(`Bundle configuration not found for bundleId: ${bundleId}`);
  }
  return config;
};
